import { SimCard } from '@/components/icons/SimCardIcon';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useSimCards } from '@/hooks/useSimCards';
import { cn } from '@/lib/utils';
import { simMapping } from '@/lib/simMapping';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Check, Info, Package, Smartphone, X } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import type { CommandLineForm } from './CommandLineView';

interface CarteSimStepProps {
  form: UseFormReturn<CommandLineForm>;
}

export function CarteSimStep({ form }: CarteSimStepProps) {
  const { data: freeSimCards = [], isLoading } = useSimCards(
    form.watch('operatorLabel') || ''
  );

  const simType = form.watch('simType');
  const physiqueOption = form.watch('physiqueOption');
  const simId = form.watch('simId');

  const selectedSim = freeSimCards.find((s) => s.id === simId);

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-slate-900">Carte SIM</h3>

      {/* SIM type selection */}
      <section className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => {
              form.setValue('simType', 'physique');
              form.setValue('physiqueOption', 'order');
            }}
            className={cn(
              'rounded-xl border p-4 text-left cursor-pointer transition-all',
              simType === 'physique'
                ? 'border-emerald-500 bg-emerald-50'
                : 'border-slate-200 hover:border-emerald-200 hover:shadow-md hover:-translate-y-0.5'
            )}
          >
            <Package className="h-5 w-5 text-slate-400 mb-2" />
            <p className="text-sm font-semibold text-slate-900">
              SIM physique
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Carte traditionnelle · 3 €
            </p>
          </button>

          <button
            type="button"
            onClick={() => {
              form.setValue('simType', 'esim');
              form.setValue('physiqueOption', 'order');
              form.setValue('simId', '');
            }}
            className={cn(
              'rounded-xl border p-4 text-left cursor-pointer transition-all',
              simType === 'esim'
                ? 'border-emerald-500 bg-emerald-50'
                : 'border-slate-200 hover:border-emerald-200 hover:shadow-md hover:-translate-y-0.5'
            )}
          >
            <div className="flex items-start justify-between">
              <Smartphone className="h-5 w-5 text-slate-400 mb-2" />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex cursor-pointer text-slate-400 hover:text-slate-600">
                      <Info className="h-3.5 w-3.5" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">Nécessite un téléphone compatible. Envoyée le jour même pour une création, le jour de la portabilité pour un changement d'opérateur.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-sm font-semibold text-slate-900">eSIM</p>
            <p className="text-xs text-slate-500 mt-1">
              Carte dématérialisée · 3 €
            </p>
          </button>
        </div>
      </section>

      {/* Physical SIM options */}
      {simType === 'physique' && (
        <section>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Mode de livraison
          </label>
          <FormField
            control={form.control}
            name="physiqueOption"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <RadioGroup
                    value={field.value}
                    onValueChange={(v) =>
                      form.setValue('physiqueOption', v as 'order' | 'stock')
                    }
                    className="space-y-2"
                  >
                    <label
                      className={cn(
                        'flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-all',
                        physiqueOption === 'order'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:border-emerald-200 hover:shadow-md hover:-translate-y-0.5'
                      )}
                    >
                      <RadioGroupItem value="order" />
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          Nouvelle carte SIM
                        </p>
                        <p className="text-xs text-slate-500">Envoi postal</p>
                      </div>
                    </label>
                    <label
                      className={cn(
                        'flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-all',
                        physiqueOption === 'stock'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:border-emerald-200 hover:shadow-md hover:-translate-y-0.5'
                      )}
                    >
                      <RadioGroupItem value="stock" />
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          SIM de mon stock
                        </p>
                        <p className="text-xs text-slate-500">
                          Sélectionner depuis l'inventaire
                        </p>
                      </div>
                    </label>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </section>
      )}

      {/* Stock SIM picker */}
      {simType === 'physique' && physiqueOption === 'stock' && (
        <FormField
          control={form.control}
          name="simId"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <section className="space-y-4">
                {field.value && selectedSim ? (
                  <div className="flex items-center gap-3 p-3 rounded-xl border border-emerald-200 bg-emerald-50">
                    <SimCard className="h-5 w-5 text-slate-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">
                        {selectedSim.operatorLabel}
                      </p>
                      <p className="text-xs text-slate-500">
                        {selectedSim.serialNumber}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                        {selectedSim.status === 'free'
                          ? 'Disponible'
                          : "En cours d'activation"}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                        {simMapping[selectedSim.type] || 'Classique'}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-slate-400 hover:text-red-500"
                        onClick={() => form.setValue('simId', '')}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Command
                    className="rounded-xl border border-slate-200"
                    filter={(value, search) => {
                      if (!search) return 1;
                      const normalizedValue = value.trim().toLowerCase();
                      const normalizedSearch = search.trim().toLowerCase();
                      return normalizedValue.includes(normalizedSearch) ? 1 : 0;
                    }}
                  >
                    <CommandInput
                      placeholder="Rechercher par ICCID ou numéro..."
                      className="h-10"
                    />
                    <CommandEmpty>
                      {isLoading ? (
                        <div className="py-6 text-center">
                          <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
                          <p className="mt-2 text-sm text-slate-500">
                            Chargement...
                          </p>
                        </div>
                      ) : (
                        <p className="py-6 text-sm text-slate-500 text-center">
                          Aucune carte SIM trouvée
                        </p>
                      )}
                    </CommandEmpty>
                    <CommandGroup className="max-h-56 overflow-auto">
                      {freeSimCards.map((sim) => (
                        <CommandItem
                          key={sim.id}
                          value={`${sim.serialNumber || ''} ${sim.operatorLabel || ''}`.trim()}
                          onSelect={() => form.setValue('simId', sim.id)}
                          className="py-2.5"
                        >
                          <div className="flex items-center gap-3 w-full">
                            <SimCard className="h-4 w-4 text-slate-400 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium text-slate-900">
                                {sim.operatorLabel}
                              </span>
                              <span className="text-xs text-slate-500 block">
                                {sim.serialNumber}
                              </span>
                            </div>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                              {simMapping[sim.type] || 'Classique'}
                            </span>
                            {field.value === sim.id && (
                              <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                )}
              </section>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
